import { ExpandMore } from '@mui/icons-material';
import { Box, List, ListItem, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import { Details } from '../Details';

export type FaqProps = {
  faqs: {
    question: ReactNode;
    answer: ReactNode;
    initiallyExpanded?: boolean;
  }[];
  canonicalUrl: string;
  defaultExpanded?: boolean;
  isReview?: boolean;
};

export function Faq({ faqs, canonicalUrl, defaultExpanded, isReview }: FaqProps) {
  return (
    <List
      sx={{ padding: 0, margin: 0, listStyle: 'none' }}
      {...(!isReview && {
        itemScope: true,
        itemType: 'https://schema.org/FAQPage',
        itemID: canonicalUrl,
      })}
    >
      {faqs.map((faq, index) => (
        <ListItem
          component="li"
          data-testid="accordion-wrapper"
          {...(!isReview && {
            itemScope: true,
            itemProp: 'mainEntity',
            itemType: 'https://schema.org/Question',
          })}
          key={`faq-question-${index}`}
          sx={{
            padding: 0,
            '&:first-of-type': {
              borderTop: '1px solid',
              borderColor: 'neutral.lighter',
            },
            borderBottom: '1px solid',
            borderColor: 'neutral.lighter',
          }}
        >
          <Details
            variant="simple"
            summaryComponentProps={{
              'data-testid': 'accordion-summary',
              ...(!isReview && { itemProp: 'name' }),
              'aria-controls': `panel-content-${index}`,
              id: `panel-header-${index}`,
              sx: {
                paddingX: 2,
                paddingY: '12px',
                '&:hover': {
                  outline: { xs: 'none', sm: '2px solid #54A0E8' },
                  bgcolor: 'blues.lightest',
                },
                WebkitTapHighlightColor: 'transparent',
              },
            }}
            SummaryComponent={
              <Box
                component="span"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Typography variant="smallHeadline" role="heading" aria-level={3}>
                  {faq.question}
                </Typography>
                <ExpandMore className="expand-icon" sx={{ opacity: 0.54, fontSize: 20 }} />
              </Box>
            }
            detailsProps={{
              ...(defaultExpanded && { open: true }),
              sx: {
                width: '100%',
                padding: 0,
              },
            }}
          >
            <Box
              data-testid="accordion-details"
              {...(!isReview && {
                itemScope: true,
                itemProp: 'acceptedAnswer',
                itemType: 'https://schema.org/Answer',
              })}
              sx={{
                padding: 3,
              }}
              id={`panel-content-${index}`}
            >
              <Box {...(!isReview && { itemProp: 'text' })}>{faq.answer}</Box>
            </Box>
          </Details>
        </ListItem>
      ))}
    </List>
  );
}
